import {
  getCountByStatus,
  useGetAllExhibitions,
} from "@/api/exhibition/exhibition";
import NewExhibitionDialog from "@/components/exhibition/NewExhibitionDialog";
import NewItemDialogTrigger from "@/components/modal/NewItemDialogTrigger";
import StatusTableTab from "@/components/StatusTableTab";
import { AdvancedSearch } from "@/components/table/AdvancedSearch";
import { AdvancedSearchChip } from "@/components/table/AdvancedSearchChip";
import { AdvancedSort } from "@/components/table/AdvancedSort";
import { GlobalSearch } from "@/components/table/GlobalSearch";
import { DataTable } from "@/components/ui/datatable";
import {
  exhibitionAdvancedSearchConfig,
  exhibitionColumns,
} from "@/config/dashboard/exhibition";
import { ColumnFormatted, generateTable } from "@/config/dashboard/general";
import { useDashboardParams } from "@/hooks/useDashboardParams";
import { Exhibition } from "@/lib/types";
import { ColumnDef, VisibilityState } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const STORAGE_KEY = "exhibition-table-preferences";

const flatVisibleExhibitionColumns = exhibitionColumns.flatMap(
  (column: ColumnFormatted) => (column.visible ? column.key : []),
);

const ExhibitionDashboard = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const {
    countByStatus,
    currentCount: totalCount,
    pagination,
    setPagination,
    fetchParams,
    filterStatus,
    setFilterStatus,
  } = useDashboardParams<Exhibition>({
    resourceName: "exhibition",
    resourceCounter: getCountByStatus,
    statusField: "expositionsPublicationStatus",
  });

  const { data } = useGetAllExhibitions(fetchParams);

  const columns: ColumnDef<Exhibition, any>[] = useMemo(() => {
    return generateTable(exhibitionColumns, "exhibition");
  }, []);

  const [columnsOrder, setColumnsOrder] = useState<string[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const { order } = JSON.parse(saved);
      return order || flatVisibleExhibitionColumns;
    }
    return flatVisibleExhibitionColumns;
  });

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const { visibility } = JSON.parse(saved);
        return (
          visibility || {
            ...exhibitionColumns.reduce(
              (acc, column) => ({
                ...acc,
                [column.key]: column.visible || false,
              }),
              {},
            ),
          }
        );
      }
      return {
        ...exhibitionColumns.reduce(
          (acc, column) => ({
            ...acc,
            [column.key]: column.visible || false,
          }),
          {},
        ),
      };
    },
  );

  return (
    <div className="flex flex-col items-start gap-3">
      <div className="flex justify-between  items-center w-full">
        <p className="text-[32px] font-[600]">
          {t("dashboardTitleExhibitions")}
        </p>
        <GlobalSearch
          config={exhibitionAdvancedSearchConfig}
          data={data ?? []}
        />
      </div>
      <div className="flex flex-row justify-between w-full">
        <StatusTableTab
          filterStatus={filterStatus}
          countByStatus={countByStatus}
          onValueChange={setFilterStatus}
        />
        <div className="flex flex-row gap-2">
          <AdvancedSearch
            config={exhibitionAdvancedSearchConfig}
            resource="exhibitions"
          />
          <AdvancedSort config={exhibitionAdvancedSearchConfig} />
          <NewItemDialogTrigger
            title={t("createEexhibition")}
            Dialog={NewExhibitionDialog}
          />
        </div>
      </div>
      <div className="w-full flex flex-row">
        <AdvancedSearchChip />
      </div>
      <div className="w-full">
        <DataTable
          onRowClick={(row) => navigate("/edit/exhibition/" + row.original.id)}
          pagination={{
            pagination: pagination,
            setPage: setPagination,
            totalSize: totalCount,
          }}
          columns={columns}
          data={data ?? []}
          storageKey={STORAGE_KEY}
          options={{
            enableColumnResizing: true,
            onColumnVisibilityChange: setColumnVisibility,
            onColumnOrderChange: setColumnsOrder,
            state: {
              columnVisibility: columnVisibility,
              columnOrder: columnsOrder,
            },
          }}
        />
      </div>
    </div>
  );
};

export default ExhibitionDashboard;
